import { DownOutlined, HomeOutlined, LeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { TWO_CHIP_ROWS, useChipRows } from "./useChipRows";
import { CHIP_GRID_BASE, CHIP_NAV_CELL, ColorChip } from "./ColorChip";
import {
  useActiveCategories,
  useMenuStations,
} from "../../../../hooks/useMenu";
import { getTranslation } from "../../../../utils/translations";
import { buildGroupColorLookup, stationColorOf } from "./catalogColors";
import { isLunchCategoryId } from "../../../../lib/lunchCatalog";

export type PreviewFilter = { kind: "station" | "group"; id: number } | null;

interface PreviewFilterChipsProps {
  /** Кое стои над прегледа: другото спрямо реда над каталога
   *  (catalogFilter = "groups" → тук станции, и обратно). */
  kind: "station" | "group";
  value: PreviewFilter;
  onChange: (value: PreviewFilter) => void;
  /** Групите на дневни менюта без видим продукт (всичко изчерпано) — без чип */
  hiddenCategoryIds?: ReadonlySet<number>;
}

/**
 * Chips-овете над „Преглед на поръчката" — всички станции или групите
 * (дърво, ниво по ниво). Кликът е ФИЛТЪР НА КАТАЛОГА (както редът над него):
 * станция → продуктите на станцията, група → групата с подгрупите ѝ;
 * къщичката го маха. Стойността живее в Ordering.tsx (selectedCategoryId /
 * selectedStationId). Групите
 * са оцветени кутийки, станциите — неутрални с точка в цвета си. Кутийките са в
 * собствения цвят (ColorChip, същият размер като реда над каталога) — `colorBy`
 * решава само фоновете в каталога.
 */
export const PreviewFilterChips = ({
  kind,
  value,
  onChange,
  hiddenCategoryIds,
}: PreviewFilterChipsProps) => {
  const { t, i18n } = useTranslation();
  const stations = useMenuStations();
  const categories = useActiveCategories().filter((category) => !hiddenCategoryIds?.has(category.id));
  const groupColor = buildGroupColorLookup(categories);

  const bySort = <T extends { sortIndex: number | null }>(list: T[]) =>
    [...list].sort(
      (a, b) =>
        (a.sortIndex ?? Number.MAX_SAFE_INTEGER) -
        (b.sortIndex ?? Number.MAX_SAFE_INTEGER),
    );
  // Групите са ДЪРВО, ниво по ниво (както CategoryChips над каталога): в
  // корена — главните групи; избереш ли група с подгрупи, главните се скриват
  // и се виждат нейните деца; стрелката назад качва едно ниво, „Всички" — в корена
  const childrenOf = (parentId: number | null) =>
    bySort(categories.filter((cat) => cat.parentId === parentId));
  const selectedCat =
    kind === "group" && value?.kind === "group"
      ? (categories.find((cat) => cat.id === value.id) ?? null)
      : null;
  const viewParent = selectedCat
    ? childrenOf(selectedCat.id).length > 0
      ? selectedCat
      : (categories.find((cat) => cat.id === selectedCat.parentId) ?? null)
    : null;

  const chips =
    kind === "station"
      ? bySort(stations).map((station) => ({
          id: station.id,
          name: getTranslation(
            station.translations,
            i18n.language,
            station.slug,
          ),
          color: stationColorOf(station),
          // Станциите са плосък списък — без подредове
          hasChildren: false,
        }))
      : childrenOf(viewParent?.id ?? null).map((cat) => ({
          id: cat.id,
          name: getTranslation(cat.translations, i18n.language, cat.slug),
          color: groupColor(cat.id),
          hasChildren: childrenOf(cat.id).length > 0,
        }));

  // Клик на chip: избор; повторен клик на активния — връща на родителя
  const handleChipClick = (id: number, active: boolean) => {
    if (kind === "station") {
      onChange(active ? null : { kind, id });
      return;
    }
    if (!active) onChange({ kind, id });
    else onChange(viewParent ? { kind, id: viewParent.id } : null);
  };

  // Много групи/станции → по подразбиране се виждат два реда, стрелката до
  // заглавието разгъва останалите. Състоянието е ЛИЧНАТА настройка
  // `sideFilterExpanded` (същата логика като реда над каталога, но отделна
  // настройка — двете секции се разгъват независимо; виж useChipRows).
  const {
    rowClipRef,
    rowOverflows,
    showAllRows: expanded,
    toggleShowAllRows,
  } = useChipRows("sideFilterExpanded", TWO_CHIP_ROWS, [
    chips.length,
    viewParent?.id,
  ]);

  return (
    <div className="px-3 pt-3 pb-2.5 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-pos-mono text-[13px] leading-[16.5px] font-semibold tracking-[.1em] uppercase text-pos-sec-head">
          {kind === "station"
            ? t("ordering.previewStations")
            : t("ordering.previewGroups")}
          <span className="text-pos-muted"> · {chips.length}</span>
        </span>
        {rowOverflows && (
          <button
            type="button"
            onClick={toggleShowAllRows}
            aria-expanded={expanded}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold text-pos-accent cursor-pointer hover:bg-pos-marked"
          >
            {t("ordering.allRows")}
            <DownOutlined
              className={`text-[10px] transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>
      <div
        ref={rowClipRef}
        // Фиксирани 3 равни колони (панелът е тесен); къщичката и стрелката
        // назад са КЛЕТКИ от мрежата като chips-овете (без празно място под
        // тях), дълго име минава на два реда и после многоточие. Свито: два
        // реда (2×40 + 4 gap) СЪС скрол вътре (без видим скролбар); „Всички"
        // до заглавието разгъва всичко и записва личната настройка
        className={`${CHIP_GRID_BASE} grid-cols-3 ${
          expanded
            ? ""
            : "max-h-[84px] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        }`}
      >
        {/* Къщичка — ресет на филтъра, същата като в реда над каталога */}
        <button
          type="button"
          title={
            kind === "station"
              ? t("ordering.allStations")
              : t("ordering.allCategories")
          }
          onClick={() => onChange(null)}
          className={CHIP_NAV_CELL}
        >
          <HomeOutlined className="text-base" />
        </button>
        {/* Стрелка назад — една стъпка нагоре по дървото (само в подниво) */}
        {kind === "group" && viewParent && (
          <button
            type="button"
            title={t("common.back")}
            onClick={() =>
              onChange(
                viewParent.parentId === null
                  ? null
                  : { kind, id: viewParent.parentId },
              )
            }
            className={CHIP_NAV_CELL}
          >
            <LeftOutlined className="text-sm" />
          </button>
        )}
        {chips.map((chip) => {
          const active = value?.kind === kind && value.id === chip.id;
          return (
            <ColorChip
              key={chip.id}
              color={chip.color}
              dot={kind === "station"}
              active={active}
              hasChildren={chip.hasChildren}
              lunch={kind === "group" && isLunchCategoryId(chip.id)}
              onClick={() => handleChipClick(chip.id, active)}
            >
              {chip.name}
            </ColorChip>
          );
        })}
      </div>
    </div>
  );
};
